import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import { ArrowLeft, Smartphone, Monitor, Tablet, Trash2, RefreshCw } from 'lucide-react';
import './DevicesScreen.css';

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)   return 'just now';
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30)  return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function DeviceIcon({ type, color }) {
  const props = { size: 22, strokeWidth: 1.75, color };
  if (type === 'mobile') return <Smartphone {...props}/>;
  if (type === 'tablet') return <Tablet {...props}/>;
  return <Monitor {...props}/>;
}

export default function DevicesScreen({ onClose }) {
  const [devices,  setDevices]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [removing, setRemoving] = useState(null);
  const [error,    setError]    = useState('');

  async function load() {
    setLoading(true); setError('');
    try {
      const d = await api.get('/api/push/devices');
      setDevices(d.devices || []);
    } catch (err) {
      setError(err.message || 'Could not load devices');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleRemove(id) {
    if (!window.confirm('Remove this device? It will no longer receive push notifications.')) return;
    setRemoving(id);
    try {
      await api.delete(`/api/push/devices/${id}`);
      setDevices(d => d.filter(x => x.id !== id));
    } catch (err) {
      alert(err.message || 'Could not remove device');
    } finally {
      setRemoving(null);
    }
  }

  const TYPE_COLORS = {
    mobile:  '#7C3AED',
    tablet:  '#0EA5E9',
    desktop: '#1B4F8A',
  };

  return (
    <div className="page">
      {/* Toolbar */}
      <div className="dev-toolbar">
        <button className="gp-back-btn" onClick={onClose}>
          <ArrowLeft size={16}/> Profile
        </button>
        <button className="dev-refresh" onClick={load} disabled={loading} title="Refresh">
          <RefreshCw size={15} className={loading ? 'spin' : ''}/>
        </button>
      </div>

      {/* Header */}
      <div className="dev-header">
        <h1 className="dev-title">My Devices</h1>
        <p className="dev-sub">
          Push notifications are sent to all registered devices below.
          Remove any you no longer use.
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="dev-error">⚠️ {error}</div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{display:'flex',justifyContent:'center',padding:48}}>
          <span className="spinner" style={{width:28,height:28}}/>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && devices.length === 0 && (
        <div className="empty-state">
          <Smartphone size={36} strokeWidth={1.25} color="var(--text-lt)"/>
          <h3>No devices registered</h3>
          <p>Open Ignite in a browser or mobile app and allow push notifications to register a device.</p>
        </div>
      )}

      {/* Device list */}
      {!loading && devices.length > 0 && (
        <>
          <p className="dev-count">
            {devices.length} registered {devices.length === 1 ? 'device' : 'devices'}
          </p>
          <div className="dev-list">
            {devices.map(device => {
              const color = TYPE_COLORS[device.type] || '#1B4F8A';
              return (
                <div key={device.id} className="dev-card card">
                  <div className="dev-card-icon" style={{background: color + '15', color}}>
                    <DeviceIcon type={device.type} color={color}/>
                  </div>
                  <div className="dev-card-body">
                    <div className="dev-card-name">{device.name}</div>
                    <div className="dev-card-meta">
                      Registered {timeAgo(device.createdAt)}
                    </div>
                    <div className="dev-card-endpoint">{device.endpoint}</div>
                  </div>
                  <button
                    className="dev-remove"
                    onClick={() => handleRemove(device.id)}
                    disabled={removing === device.id}
                    title="Remove device"
                  >
                    {removing === device.id
                      ? <span className="spinner" style={{width:14,height:14}}/>
                      : <Trash2 size={16}/>}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="dev-footnote">
            Devices are automatically removed if they become unreachable (e.g. after uninstalling the app).
            You can also remove a device manually to stop receiving notifications on it.
          </p>
        </>
      )}
    </div>
  );
}
